import React from 'react';
import ProfileShell from '../../components/forms/ProfileShell';
import { SectionHeader, Field, Input, Select, TextArea, RadioGroup, Toggle } from '../../components/forms/FormPrimitives';

const PROTECTION_GOALS = [
  'Protect my family',
  'Mortgage protection',
  'Final expenses',
  'Business protection',
  'Retirement planning',
  'Legacy planning',
  'Not sure yet',
];

const COVERAGE_AMOUNTS = ['$100,000', '$250,000', '$500,000', '$1,000,000', '$2,000,000+', 'Not sure yet'];
const COVERAGE_LENGTHS = ['10 years', '15 years', '20 years', '25 years', '30 years', 'Whole life', 'Not sure yet'];
const BUDGETS = ['Under $25/mo', '$25 – $50/mo', '$50 – $100/mo', '$100 – $250/mo', '$250+/mo', 'Not sure yet'];
const HEALTH = ['Excellent', 'Good', 'Average', 'Below average'];
const GENDERS = ['Male', 'Female', 'Prefer not to say'];

const LifeProfile = () => {
  const sections = [
    {
      title: 'Your Protection Goal',
      render: (data, update) => (
        <div>
          <SectionHeader
            index={1}
            title="What are you looking to protect?"
            subtitle="Life insurance is personal. Start with what matters most — we'll go from there."
          />
          <Field label="Main reason for coverage" testid="life-main-reason">
            <RadioGroup
              name="life_goal"
              value={data.main_reason}
              onChange={(v) => update({ main_reason: v })}
              options={PROTECTION_GOALS}
            />
          </Field>
        </div>
      ),
    },
    {
      title: 'Coverage Preferences',
      render: (data, update) => (
        <div>
          <SectionHeader index={2} title="Coverage Preferences" subtitle="Help us narrow down options that fit your goals and budget." />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Desired coverage amount" testid="life-amount">
              <Select value={data.coverage_amount} onChange={(v) => update({ coverage_amount: v })} options={COVERAGE_AMOUNTS} />
            </Field>
            <Field label="Coverage length" testid="life-length">
              <Select value={data.coverage_length} onChange={(v) => update({ coverage_length: v })} options={COVERAGE_LENGTHS} />
            </Field>
            <Field label="Monthly budget range" testid="life-budget">
              <Select value={data.budget_range} onChange={(v) => update({ budget_range: v })} options={BUDGETS} />
            </Field>
            <Field label="Existing life insurance?" testid="life-existing">
              <Select value={data.existing_life} onChange={(v) => update({ existing_life: v })} options={['Yes', 'No']} />
            </Field>
          </div>
          <Field label="Who would you primarily like to protect?" testid="life-beneficiary">
            <Input value={data.beneficiary_goal} onChange={(v) => update({ beneficiary_goal: v })} placeholder="e.g. Spouse and children" />
          </Field>
        </div>
      ),
    },
    {
      title: 'Personal Information',
      render: (data, update) => (
        <div>
          <SectionHeader index={3} title="Personal Information" subtitle="Share what you're comfortable with — these help with accurate quotes." />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Date of birth" testid="life-dob">
              <Input type="date" value={data.dob} onChange={(v) => update({ dob: v })} />
            </Field>
            <Field label="Gender" testid="life-gender">
              <Select value={data.gender} onChange={(v) => update({ gender: v })} options={GENDERS} />
            </Field>
            <Field label="Height" testid="life-height">
              <Input value={data.height} onChange={(v) => update({ height: v })} placeholder="e.g. 5'10&quot;" />
            </Field>
            <Field label="Weight (lbs)" testid="life-weight">
              <Input value={data.weight} onChange={(v) => update({ weight: v })} placeholder="180" />
            </Field>
          </div>
          <div className="mt-4">
            <Toggle
              value={data.tobacco_use}
              onChange={(v) => update({ tobacco_use: v })}
              label="I currently use tobacco or nicotine products"
              testid="life-tobacco"
            />
          </div>
        </div>
      ),
    },
    {
      title: 'General Health',
      render: (data, update) => (
        <div>
          <SectionHeader index={4} title="General Health" subtitle="Optional — share what you're comfortable with." />
          <Field label="General health status" testid="life-health-status">
            <RadioGroup
              name="health"
              value={data.health_status}
              onChange={(v) => update({ health_status: v })}
              options={HEALTH}
            />
          </Field>
          <Field label="Current medications (optional)" testid="life-meds">
            <TextArea value={data.medications} onChange={(v) => update({ medications: v })} placeholder="If any" />
          </Field>
          <Field label="Major health conditions, if any (optional)" testid="life-conditions">
            <TextArea value={data.health_conditions} onChange={(v) => update({ health_conditions: v })} placeholder="If any" />
          </Field>
          <p className="text-xs text-gray-500 italic mt-2">
            Life insurance is not just about a policy. It is about protecting the people, responsibilities, and future that matter most.
          </p>
        </div>
      ),
    },
  ];

  return (
    <ProfileShell
      coverageType="life"
      sections={sections}
      intro="Life insurance is personal. Start with what you are comfortable sharing. We will guide you step by step."
      personalTouchMessage="Life insurance is personal. Start with what you are comfortable sharing — we will guide you step by step. The more details you provide, the more accurate your quote or coverage review may be."
    />
  );
};

export default LifeProfile;
